import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { suivreBudgets } from '../../analysis/budgets'
import { chargeReelle, creances, nomCategorie } from '../../analysis/donnees'
import { deriverEcheances } from '../../analysis/recurrences'
import { mouvements, soldeAu } from '../../analysis/soldes'
import { somme } from '../../domain/argent'
import {
  ajouterJours,
  aujourdhui,
  capitaliser,
  formatJourCourt,
  formatMois,
  moisCourant,
} from '../../domain/temps'
import {
  Carte,
  Encart,
  Entete,
  IconeChevron,
  IconePlus,
  Jauge,
  Montant,
  Pastille,
  Puce,
} from '../composants'
import { estInstallee } from '../../pwa/register'
import { useDonnees } from '../donnees'

export function PageTableauDeBord() {
  const { d, ix } = useDonnees()
  const navigate = useNavigate()
  const jour = aujourdhui()
  const mois = moisCourant()

  const vue = useMemo(() => {
    const mvts = mouvements(ix)
    const comptes = d.comptes.filter((c) => !c.archive).map((c) => ({ compte: c, solde: soldeAu(c, mvts, jour) }))

    // Echeances des 14 prochains jours, plus celles en retard depuis 60 jours.
    const echeances = deriverEcheances(d.recurrences, d.exceptions, d.operations, ajouterJours(jour, -60), ajouterJours(jour, 14), jour).filter(
      (e) => e.statut === 'prevue',
    )

    const ouvertes = creances(ix).filter((c) => c.statut !== 'soldee')
    const parTiers = new Map<string, number>()
    for (const c of ouvertes) parTiers.set(c.part.tiersId, (parTiers.get(c.part.tiersId) ?? 0) + c.restant)

    const budgets = suivreBudgets(ix, mois, jour, null).sort((a, b) => b.taux - a.taux)
    const opsMois = d.operations.filter((o) => o.date.startsWith(mois))
    return {
      comptes,
      total: somme(comptes.map((c) => c.solde)),
      echeances,
      aRecevoir: [...parTiers.entries()].sort((a, b) => b[1] - a[1]),
      totalARecevoir: somme(ouvertes.map((c) => c.restant)),
      budgets,
      depensesMois: somme(opsMois.filter((o) => o.sens === 'depense').map((o) => chargeReelle(ix, o))),
      revenusMois: somme(opsMois.filter((o) => o.sens === 'revenu').map((o) => o.montant)),
    }
  }, [d, ix, jour, mois])

  return (
    <>
      <Entete titre="Budget" sous={capitaliser(formatMois(mois))} />
      <main className="contenu">
        <BandeauInstallation />
        <Carte titre="Comptes" action={<Link to="/comptes" className="btn btn--fantome btn--petit">Gérer</Link>}>
          <ul className="liste">
            {vue.comptes.map(({ compte, solde }) => (
              <li key={compte.id} className="ligne ligne--espacee">
                <span className="ligne">
                  <Pastille couleur={compte.couleur} />
                  {compte.nom}
                </span>
                <Montant valeur={solde} />
              </li>
            ))}
          </ul>
          {vue.comptes.length > 1 && (
            <>
              <hr className="separateur" style={{ margin: '10px 0' }} />
              <div className="ligne ligne--espacee gras">
                <span>Total</span>
                <Montant valeur={vue.total} />
              </div>
            </>
          )}
        </Carte>

        <Carte titre="Ce mois-ci">
          <div className="mesures" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="mesure">
              <div className="mesure__valeur"><Montant valeur={vue.depensesMois} /></div>
              <div className="mesure__libelle">dépenses à ma charge</div>
            </div>
            <div className="mesure">
              <div className="mesure__valeur"><Montant valeur={vue.revenusMois} /></div>
              <div className="mesure__libelle">revenus</div>
            </div>
          </div>
          {vue.budgets.length > 0 && (
            <div className="pile" style={{ marginTop: 12 }}>
              {vue.budgets.slice(0, 4).map((s) => (
                <div key={s.budget.id}>
                  <div className="ligne ligne--espacee doux">
                    <span>{nomCategorie(ix, s.budget.categorieId)}{s.budget.periode === 'annuel' ? ' (année)' : ''}</span>
                    <span className="mono">
                      <Montant valeur={s.consomme} /> / <Montant valeur={s.plafond} />
                    </span>
                  </div>
                  <Jauge taux={s.taux} ecoule={s.ecoule} etat={s.etat} />
                </div>
              ))}
              <Link to="/previsionnel" className="btn btn--fantome btn--petit">Prévisionnel du mois</Link>
            </div>
          )}
        </Carte>

        {vue.totalARecevoir > 0 && (
          <Carte titre="À recevoir" action={<Link to="/remboursements" className="btn btn--fantome btn--petit">Détail</Link>}>
            <ul className="liste">
              {vue.aRecevoir.map(([tiersId, montant]) => (
                <li key={tiersId}>
                  <Link to={`/remboursements?tiers=${tiersId}`} className="item">
                    <span className="grandir">{ix.tiersParId.get(tiersId)?.nom ?? '—'}</span>
                    <Montant valeur={montant} />
                    <span className="chevron"><IconeChevron /></span>
                  </Link>
                </li>
              ))}
            </ul>
          </Carte>
        )}

        <Carte titre="Échéances" action={<Link to="/recurrences" className="btn btn--fantome btn--petit">Toutes</Link>}>
          {vue.echeances.length === 0 ? (
            <p className="tres-doux">Aucune échéance dans les deux prochaines semaines.</p>
          ) : (
            <ul className="liste">
              {vue.echeances.slice(0, 8).map((e) => (
                <li key={`${e.recurrence.id}${e.occurrence}`}>
                  <Link to={`/recurrences?mois=${e.occurrence.slice(0, 7)}`} className="item">
                    <div className="grandir">
                      <div className="item__principal">{e.recurrence.libelle}</div>
                      <div className="item__secondaire">
                        {formatJourCourt(e.occurrence)} · {ix.compteParId.get(e.recurrence.compteId)?.nom}
                      </div>
                    </div>
                    {e.enRetard && <Puce ton="alerte">à saisir</Puce>}
                    <Montant valeur={e.montant} sens={e.recurrence.sens} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Carte>

        {d.operations.length === 0 && (
          <Encart>
            Commencez par vérifier vos <Link to="/comptes">comptes</Link> (solde de départ), puis saisissez une première
            opération avec le bouton <strong>+</strong>. Les prélèvements réguliers se déclarent dans{' '}
            <Link to="/recurrences">Récurrences</Link>.
          </Encart>
        )}
      </main>
      <button type="button" className="fab" aria-label="Nouvelle opération" onClick={() => navigate('/operations/nouvelle')}>
        <IconePlus />
      </button>
    </>
  )
}

// ---------------------------------------------------------------------------
// Bandeau d'installation
// ---------------------------------------------------------------------------

const CLE_MASQUE = 'bandeauInstallationMasqueLe'
const DELAI_RAPPEL_MS = 7 * 24 * 3600 * 1000

/**
 * Rappelle d'installer l'app tant qu'elle tourne dans un onglet.
 *
 * Ce n'est pas du confort : dans un onglet Safari, iOS peut purger IndexedDB
 * apres quelques semaines sans visite, et avec lui tout l'historique du budget.
 * Masquer le bandeau ne le fait donc taire qu'une semaine, puisque le risque,
 * lui, demeure.
 *
 * La date de masquage vit dans `localStorage` et non dans la base : c'est une
 * preference propre a ce navigateur, qui n'a rien a faire dans une sauvegarde.
 * Tout acces est protege — en navigation privee, le stockage peut lever.
 */
function BandeauInstallation() {
  const [visible, setVisible] = useState(() => {
    if (estInstallee()) return false
    try {
      const masque = Number(localStorage.getItem(CLE_MASQUE))
      return !masque || Date.now() - masque > DELAI_RAPPEL_MS
    } catch {
      return true
    }
  })

  if (!visible) return null

  const masquer = () => {
    try {
      localStorage.setItem(CLE_MASQUE, String(Date.now()))
    } catch {
      // Sans stockage, le bandeau reviendra au prochain chargement : acceptable.
    }
    setVisible(false)
  }

  return (
    <Encart ton="alerte">
      <div className="pile pile--serree">
        <span>
          <strong>Installez l’app sur l’écran d’accueil.</strong> Ouverte dans Safari, ses données peuvent être effacées après
          quelques semaines sans visite.
        </span>
        <div className="boutons">
          <Link to="/installation" className="btn btn--petit btn--principal">Comment faire</Link>
          <button type="button" className="btn btn--petit" onClick={masquer}>Plus tard</button>
        </div>
      </div>
    </Encart>
  )
}
